import React, { useEffect, useMemo, useRef, useState } from "react";
import useAxios from "../../utils/useAxios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Modal,
  Radio,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { useToast } from "@chakra-ui/react";
import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useBreadcrumbs } from "../../context/BreadcrumbContext";

const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "png",
  "jpg",
  "jpeg",
];
const ALLOWED_EXTENSION_SET = new Set(ALLOWED_EXTENSIONS);
const FILE_INPUT_ACCEPT = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",");
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const DELIVERABLES_URL = "/deliverables/";
const EQUIPMENT_LIST_URL = "/equipment/";
const PARSE_REGISTER_SPREADSHEET_URL = "/deliverables/parse-register-spreadsheet/";
const REGISTER_TEMPLATE_URL = "/deliverables/register-template/";
const EXISTING_DOCUMENTS_URL = "/deliverables/existing-register-entries/";
const REGISTER_TEMPLATE_FILENAME = "document_register_upload_template.xlsx";
const BULK_EDIT_HELPER_TEXT =
  "Use bulk edit to apply Discipline Type, Document Type, or Revision No. to all or selected documents.";
const NO_SELECTED_ROWS_MESSAGE = "Please select at least one document row.";
// Asset-matching (link/create Equipment by tagNumber) only makes sense when
// this modal spans multiple assets - Plant or Site scope. At narrower scopes
// (equipment/tier/component/instrument) it would just be a self-match, so
// the whole section stays hidden there. See
// plans/document_asset_bulk/document-asset-bulk-upload.md.
const ASSET_MATCHING_SCOPES = new Set(["plant", "site"]);
// Spreadsheet-mapped fields that already have a slot on the document row
// today (some, like area/voteNo, aren't shown as their own table column but
// are still sent to the backend - see buildSelectedFileItem).
const SPREADSHEET_TARGET_TO_ROW_FIELD = {
  client_drawing_no: "clientDrawingNo",
  description: "description",
  revision_no: "revNumber",
  area: "area",
  vote_no: "voteNo",
};
const IGNORE_MAPPING_VALUE = "ignore";
// Fields the recompute effect is allowed to auto-backfill from the
// spreadsheet/matched asset - once the user explicitly sets one (via a
// table control or Bulk Edit), it's marked dirty here and never
// auto-overwritten again, regardless of upload order.
const DIRTY_TRACKABLE_FIELDS = new Set([
  "description",
  "discipline",
  "documentType",
  "equipmentType",
  "revNumber",
  "area",
  "voteNo",
  "createAssetIfUnmatched",
]);

const createLocalId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const buildDefaultMetadata = () => ({
  clientDrawingNo: "",
  description: "",
  discipline: "",
  documentType: "",
  revNumber: "0",
});

const buildBulkEditState = () => ({
  discipline: "",
  documentType: "",
  revNumber: "",
  equipmentType: "",
});

const normalizeText = (value) => `${value ?? ""}`.trim();

const isFileLike = (file) => {
  if (!file) return false;
  if (typeof File !== "undefined" && file instanceof File) return true;
  return typeof file.name === "string" && typeof file.size === "number";
};

const formatFileSize = (bytes = 0) => {
  if (!bytes) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
};

const getFileExtension = (fileName = "") => {
  if (!fileName.includes(".")) return "";
  return fileName.split(".").pop().toLowerCase();
};

const inferDisciplineName = (fileName = "") => {
  const disciplineMatch = fileName.match(/(?:\s|_|-)(\d+)(?:\s|_|-)(\d+)/);
  if (!disciplineMatch) return "";

  switch (disciplineMatch[2]) {
    case "08":
      return "Control & Instrumentation";
    case "05":
    case "15":
      return "Process";
    case "09":
      return "Electrical";
    default:
      return "";
  }
};

const inferArea = (fileName = "") => {
  const areaMatch = fileName.match(/(?:\s|_|-)(\d+)(?:\s|_|-)/);
  return areaMatch ? areaMatch[1] : "";
};

const normalizeMatchKey = (value) => `${value ?? ""}`.trim().toLowerCase();

const buildEquipmentTagIndex = (equipmentList) => {
  const index = new Map();
  equipmentList.forEach((equipment) => {
    const key = normalizeMatchKey(equipment.tagNumber);
    if (!key) return;
    if (!index.has(key)) index.set(key, []);
    index.get(key).push(equipment);
  });
  return index;
};

const buildSpreadsheetRowIndex = (rows, documentNoHeader) => {
  const index = new Map();
  if (!documentNoHeader) return index;

  rows.forEach((row) => {
    const key = normalizeMatchKey(row[documentNoHeader]);
    if (key && !index.has(key)) {
      index.set(key, row);
    }
  });

  return index;
};

// Resolves what should happen for a file/row given its current lookup key
// (its own Client Drawing No.) against the equipment tag index and the
// spreadsheet row index - mirrors the server-side rule in
// server/FrequencyShift/document_register/asset_matching.py so the preview
// shown here matches what bulk_upload will actually do.
const resolveAssetStatusForKey = (lookupKey, { equipmentTagIndex, spreadsheetRowIndex, canCreateAssets }) => {
  const key = normalizeMatchKey(lookupKey);
  if (!key) {
    return {
      assetStatus: "none",
      assetCandidates: [],
      matchedEquipmentId: "",
      matchedEquipment: null,
      spreadsheetRow: null,
    };
  }

  const candidates = equipmentTagIndex.get(key) || [];
  if (candidates.length === 1) {
    return {
      assetStatus: "existing",
      assetCandidates: [],
      matchedEquipmentId: String(candidates[0].id),
      matchedEquipment: candidates[0],
      spreadsheetRow: spreadsheetRowIndex.get(key) || null,
    };
  }
  if (candidates.length > 1) {
    return {
      assetStatus: "ambiguous",
      assetCandidates: candidates,
      matchedEquipmentId: "",
      matchedEquipment: null,
      spreadsheetRow: spreadsheetRowIndex.get(key) || null,
    };
  }

  const spreadsheetRow = spreadsheetRowIndex.get(key) || null;
  if (spreadsheetRow && canCreateAssets) {
    return {
      assetStatus: "create",
      assetCandidates: [],
      matchedEquipmentId: "",
      matchedEquipment: null,
      spreadsheetRow,
    };
  }

  return {
    assetStatus: "none",
    assetCandidates: [],
    matchedEquipmentId: "",
    matchedEquipment: null,
    spreadsheetRow,
  };
};

// Indexes existing-document entries (from GET deliverables/existing-register-entries/)
// by target ("linkType:objectId") then by "name|revNumber" - a free,
// synchronous lookup per row, mirroring buildEquipmentTagIndex one level
// deeper (one extra key for the target a document is linked to).
const buildExistingDocumentIndex = (entries) => {
  const index = new Map();
  entries.forEach((entry) => {
    const targetKey = `${entry.linkType}:${entry.objectId}`;
    const nameRevKey = `${normalizeMatchKey(entry.name)}|${normalizeMatchKey(entry.revNumber)}`;
    if (!index.has(targetKey)) index.set(targetKey, new Map());
    index.get(targetKey).set(nameRevKey, entry);
  });
  return index;
};

// Resolves whether this item's Client Drawing No. + revision already
// exists on any target it would link to (its base parent(s), plus the
// matched Equipment when asset-matching resolved one) - checked against
// existingDocumentIndex, built once from a single upfront fetch, so this
// is a free lookup on every keystroke/re-render, no network call per row.
// Mirrors resolveAssetStatusForKey's role for asset matching.
const findDuplicateForItem = (
  item,
  { existingDocumentIndex, baseTargets, isAssetMatchingEnabled },
) => {
  const nameRevKey = `${normalizeMatchKey(item.clientDrawingNo)}|${normalizeMatchKey(item.revNumber)}`;
  if (!normalizeMatchKey(item.clientDrawingNo)) return null;

  const targets = [...baseTargets];
  if (
    isAssetMatchingEnabled &&
    item.assetStatus === "existing" &&
    item.matchedEquipmentId &&
    !item.assetLinkSkipped
  ) {
    targets.push({ linkType: "equipment", objectId: item.matchedEquipmentId });
  }

  for (const target of targets) {
    const targetKey = `${target.linkType}:${target.objectId}`;
    const match = existingDocumentIndex.get(targetKey)?.get(nameRevKey);
    if (match) return match;
  }

  return null;
};

// Best-effort: resolves a spreadsheet-mapped Discipline/Document Type NAME
// to an existing option's id. Unlike the table's own MetadataAutocomplete,
// this never creates a new option on the user's behalf - an unmatched name
// is left for the user to pick/create explicitly in the row, same as an
// unmapped column would be.
const resolveOptionIdByName = (options, name) => {
  const normalized = normalizeMatchKey(name);
  if (!normalized) return "";
  const match = options.find(
    (option) => normalizeMatchKey(option.name) === normalized,
  );
  return match ? match.id : "";
};

// Applies a matched document-register spreadsheet row to a document row's
// metadata, per the column mapping. Only returns overrides for columns that
// are actually mapped (not "Ignore") and have a value, so it never clobbers
// a field with a blank. "equipment_type" (labeled "Asset Type" in the UI,
// matching Equipment.equipmentType - the same field NewEquipment.jsx's own
// "Asset Type" field writes to) only ever matters for a row that ends up
// creating a new asset (see buildSelectedFileItem) - it's harmless but
// unused otherwise. "deliverable_status" and "project" targets are
// selectable in the mapping UI (they exist on the backend/model) but are
// not yet applied here - see the plan's Phase 4 notes.
const applyMappedSpreadsheetRow = ({
  spreadsheetRow,
  columnMapping,
  disciplineTypes,
  deliverableTypes,
  equipmentTypes,
}) => {
  if (!spreadsheetRow) return {};

  const overrides = {};

  Object.entries(columnMapping).forEach(([header, target]) => {
    if (!target || target === IGNORE_MAPPING_VALUE) return;

    const rawValue = normalizeText(spreadsheetRow[header]);
    if (!rawValue) return;

    if (target === "discipline_type") {
      const matchedId = resolveOptionIdByName(disciplineTypes, rawValue);
      if (matchedId) overrides.discipline = matchedId;
      return;
    }

    if (target === "document_type") {
      const matchedId = resolveOptionIdByName(deliverableTypes, rawValue);
      if (matchedId) overrides.documentType = matchedId;
      return;
    }

    if (target === "equipment_type") {
      const matchedId = resolveOptionIdByName(equipmentTypes, rawValue);
      if (matchedId) overrides.equipmentType = matchedId;
      return;
    }

    const rowField = SPREADSHEET_TARGET_TO_ROW_FIELD[target];
    if (rowField) {
      overrides[rowField] = rawValue;
    }
  });

  return overrides;
};

const flattenErrors = (errors) => {
  if (!errors) return "";
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.map(flattenErrors).filter(Boolean).join(" ");
  if (typeof errors === "object") {
    return Object.values(errors)
      .map(flattenErrors)
      .filter(Boolean)
      .join(" ");
  }
  return "";
};

const getFileValidationErrors = (file) => {
  const errors = {};

  if (!isFileLike(file)) {
    errors.file = "A valid file is required.";
    return errors;
  }

  const extension = getFileExtension(file?.name);

  if (!ALLOWED_EXTENSION_SET.has(extension)) {
    errors.file = "Unsupported file format.";
  }

  if ((file?.size || 0) > MAX_FILE_SIZE_BYTES) {
    errors.fileSize = `File exceeds ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.`;
  }

  return errors;
};

const getMetadataValidationErrors = (item) => {
  const errors = {};

  if (!normalizeText(item.clientDrawingNo)) {
    errors.clientDrawingNo = "Client Drawing No. is required.";
  }
  if (!normalizeText(item.description)) {
    errors.description = "Description is required.";
  }
  if (!item.discipline) {
    errors.discipline = "Discipline Type is required.";
  }
  if (!item.documentType) {
    errors.documentType = "Document Type is required.";
  }
  if (
    item.assetStatus === "ambiguous" &&
    !item.matchedEquipmentId &&
    !item.assetLinkSkipped
  ) {
    errors.asset =
      "Multiple assets share this tag - pick one or choose \"No asset\".";
  }
  if (
    item.assetStatus === "create" &&
    item.createAssetIfUnmatched &&
    !item.equipmentType
  ) {
    errors.equipmentType = "Asset Type is required to create a new asset.";
  }
  if (item.duplicateOf) {
    errors.duplicate =
      `A document numbered '${item.duplicateOf.name}'` +
      (item.duplicateOf.revNumber
        ? ` revision '${item.duplicateOf.revNumber}'`
        : "") +
      " is already linked here.";
  }

  return errors;
};

const buildValidationState = (item) => {
  const validationErrors = {
    ...getFileValidationErrors(item.file),
    ...getMetadataValidationErrors(item),
    ...(item.serverErrors || {}),
  };

  return {
    ...item,
    validationErrors,
    validationStatus:
      Object.keys(validationErrors).length > 0 ? "invalid" : "valid",
  };
};

const resolveOptionValue = (options, value) =>
  options.find((option) => String(option.id) === String(value)) || null;

const filterCreatableOptions = (options, params) => {
  const filtered = options.filter((option) =>
    option.name.toLowerCase().includes(params.inputValue.toLowerCase()),
  );

  if (params.inputValue.trim() !== "" && filtered.length === 0) {
    filtered.push({
      id: "__new__",
      name: `Add "${params.inputValue.trim()}"`,
      inputValue: params.inputValue.trim(),
    });
  }

  return filtered;
};

const MetadataAutocomplete = ({
  label,
  value,
  options,
  onChange,
  onCreate,
  disabled = false,
  minWidth = 150,
  allowCreate = true,
}) => {
  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={resolveOptionValue(options, value)}
      filterOptions={allowCreate ? filterCreatableOptions : undefined}
      disabled={disabled}
      onChange={async (_, newValue) => {
        if (!newValue) {
          onChange("");
          return;
        }

        if (allowCreate && newValue.id === "__new__") {
          const created = await onCreate(newValue.inputValue);
          if (created) {
            onChange(created.id);
          }
          return;
        }

        onChange(newValue.id);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
            minWidth,
          }}
        />
      )}
    />
  );
};

const AddDocuments = ({ object, id, fetchData }) => {
  const api = useAxios();
  const toast = useToast();
  const { breadcrumbs } = useBreadcrumbs();
  const { siteId, plantId, projectId } = useParams();

  const [disciplineTypes, setDisciplineTypes] = useState([]);
  const [deliverableTypes, setDeliverableTypes] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [defaultMetadata, setDefaultMetadata] = useState(buildDefaultMetadata);
  const [bulkEdit, setBulkEdit] = useState(buildBulkEditState);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Asset-matching (see plans/document_asset_bulk/document-asset-bulk-upload.md,
  // Phase 4) - only active at Plant/Site scope, see ASSET_MATCHING_SCOPES.
  const [equipmentList, setEquipmentList] = useState([]);
  const [existingDocumentEntries, setExistingDocumentEntries] = useState([]);
  const [baseTargets, setBaseTargets] = useState([]);
  const [spreadsheetFile, setSpreadsheetFile] = useState(null);
  const [spreadsheetHeaders, setSpreadsheetHeaders] = useState([]);
  const [spreadsheetRows, setSpreadsheetRows] = useState([]);
  const [spreadsheetPreviewRows, setSpreadsheetPreviewRows] = useState([]);
  const [spreadsheetTotalRowCount, setSpreadsheetTotalRowCount] = useState(0);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [availableTargetFields, setAvailableTargetFields] = useState({});
  const [columnMapping, setColumnMapping] = useState({});
  const [isParsingSpreadsheet, setIsParsingSpreadsheet] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [spreadsheetError, setSpreadsheetError] = useState(null);

  const fileInputRef = useRef(null);
  const spreadsheetInputRef = useRef(null);

  const assetMatchingScope = ASSET_MATCHING_SCOPES.has(object) ? object : null;
  const isAssetMatchingEnabled = Boolean(assetMatchingScope);
  const canCreateAssets = assetMatchingScope === "plant";

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [disciplineRes, deliverableTypeRes, equipmentTypeRes] = await Promise.all([
          api.get("/discipline-types/"),
          api.get("/deliverable-types/"),
          api.get("/equipment-types/"),
        ]);

        setDisciplineTypes(disciplineRes.data.results);
        setDeliverableTypes(deliverableTypeRes.data.results);
        setEquipmentTypes(equipmentTypeRes.data.results);
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchOptions();
  }, [api]);

  useEffect(() => {
    if (!disciplineTypes.length) return;

    setSelectedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (item.discipline || !item.suggestedDiscipline) {
          return buildValidationState(item);
        }

        const matchedDiscipline = disciplineTypes.find(
          (option) =>
            option.name.toLowerCase() === item.suggestedDiscipline.toLowerCase(),
        );

        return buildValidationState({
          ...item,
          discipline: matchedDiscipline?.id || item.discipline,
        });
      }),
    );
  }, [disciplineTypes]);

  useEffect(() => {
    if (!isAssetMatchingEnabled || !id) {
      setEquipmentList([]);
      return;
    }

    let isCancelled = false;

    const fetchAllEquipment = async () => {
      const scopeParam = assetMatchingScope === "plant" ? { plant: id } : { site: id };
      const results = [];
      let nextUrl = EQUIPMENT_LIST_URL;
      let params = scopeParam;

      try {
        while (nextUrl) {
          const response = await api.get(nextUrl, { params });
          const data = response.data;
          results.push(...(data?.results || (Array.isArray(data) ? data : [])));
          nextUrl = data?.next || null;
          params = undefined;
        }

        if (!isCancelled) {
          setEquipmentList(results);
        }
      } catch (error) {
        console.error("Error fetching equipment for asset matching:", error);
        if (!isCancelled) {
          setEquipmentList([]);
        }
      }
    };

    fetchAllEquipment();

    return () => {
      isCancelled = true;
    };
  }, [api, isAssetMatchingEnabled, assetMatchingScope, id]);

  const equipmentTagIndex = useMemo(
    () => buildEquipmentTagIndex(equipmentList),
    [equipmentList],
  );

  // Fetched once per parent context (not gated on isAssetMatchingEnabled -
  // duplicate checking against the base Plant/Site/Project parent is
  // useful even without asset matching), then checked client-side per row
  // via findDuplicateForItem - same "fetch the whole scope once, index it,
  // check for free" shape as the equipment-list fetch above.
  useEffect(() => {
    if (!object || !id) {
      setExistingDocumentEntries([]);
      setBaseTargets([]);
      return;
    }

    let isCancelled = false;

    const fetchExistingDocuments = async () => {
      const payload = { parent_type: object, parent_id: id };
      const resolvedSiteId = siteId || breadcrumbs?.site?.id;
      const resolvedPlantId = plantId || breadcrumbs?.plant?.id;
      const resolvedProjectId = projectId || breadcrumbs?.project?.id;
      if (resolvedSiteId) payload.site_id = resolvedSiteId;
      if (resolvedPlantId) payload.plant_id = resolvedPlantId;
      if (resolvedProjectId) payload.project_id = resolvedProjectId;

      try {
        const response = await api.post(EXISTING_DOCUMENTS_URL, payload);
        if (!isCancelled) {
          setExistingDocumentEntries(response.data?.entries || []);
          setBaseTargets(response.data?.baseTargets || []);
        }
      } catch (error) {
        console.error("Error fetching existing documents for duplicate check:", error);
        if (!isCancelled) {
          setExistingDocumentEntries([]);
          setBaseTargets([]);
        }
      }
    };

    fetchExistingDocuments();

    return () => {
      isCancelled = true;
    };
  }, [
    api,
    object,
    id,
    siteId,
    plantId,
    projectId,
    breadcrumbs?.site?.id,
    breadcrumbs?.plant?.id,
    breadcrumbs?.project?.id,
  ]);

  const existingDocumentIndex = useMemo(
    () => buildExistingDocumentIndex(existingDocumentEntries),
    [existingDocumentEntries],
  );

  const documentNoColumnHeader = useMemo(
    () =>
      Object.entries(columnMapping).find(
        ([, target]) => target === "client_drawing_no",
      )?.[0] || null,
    [columnMapping],
  );

  const spreadsheetRowIndex = useMemo(
    () => buildSpreadsheetRowIndex(spreadsheetRows, documentNoColumnHeader),
    [spreadsheetRows, documentNoColumnHeader],
  );

  // Recomputes every row's asset-match preview AND its spreadsheet-derived
  // metadata whenever the inputs that feed either one change - the
  // equipment list can finish loading, or the spreadsheet can be
  // uploaded/mapped, after files were already added (documents-first
  // order), so this can't only run at file-add time (buildSelectedFileItem)
  // the way it originally did. Each backfillable field is only overwritten
  // when the user hasn't already touched it (item.dirtyFields), so a manual
  // edit is never clobbered regardless of which order things were uploaded
  // in. Mirrors the discipline-backfill effect above, extended to cover
  // every spreadsheet-mappable field plus the "Create asset" default.
  useEffect(() => {
    if (!isAssetMatchingEnabled) return;

    setSelectedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (item.uploadStatus === "uploaded") return item;

        const resolution = resolveAssetStatusForKey(item.clientDrawingNo, {
          equipmentTagIndex,
          spreadsheetRowIndex,
          canCreateAssets,
        });

        const spreadsheetOverrides = applyMappedSpreadsheetRow({
          spreadsheetRow: resolution.spreadsheetRow,
          columnMapping,
          disciplineTypes,
          deliverableTypes,
          equipmentTypes,
        });
        const matchedAssetDescription = normalizeText(
          resolution.matchedEquipment?.description,
        );

        const dirty = item.dirtyFields || {};
        const fieldUpdates = {};

        if (!dirty.description) {
          const nextDescription =
            spreadsheetOverrides.description || matchedAssetDescription;
          if (nextDescription) fieldUpdates.description = nextDescription;
        }
        if (!dirty.discipline && spreadsheetOverrides.discipline) {
          fieldUpdates.discipline = spreadsheetOverrides.discipline;
        }
        if (!dirty.documentType && spreadsheetOverrides.documentType) {
          fieldUpdates.documentType = spreadsheetOverrides.documentType;
        }
        if (!dirty.equipmentType && spreadsheetOverrides.equipmentType) {
          fieldUpdates.equipmentType = spreadsheetOverrides.equipmentType;
        }
        if (!dirty.revNumber && spreadsheetOverrides.revNumber) {
          fieldUpdates.revNumber = spreadsheetOverrides.revNumber;
        }
        if (!dirty.area && spreadsheetOverrides.area) {
          fieldUpdates.area = spreadsheetOverrides.area;
        }
        if (!dirty.voteNo && spreadsheetOverrides.voteNo) {
          fieldUpdates.voteNo = spreadsheetOverrides.voteNo;
        }

        return buildValidationState({
          ...item,
          ...resolution,
          ...fieldUpdates,
          createAssetIfUnmatched:
            resolution.assetStatus === "create"
              ? dirty.createAssetIfUnmatched
                ? item.createAssetIfUnmatched
                : true
              : false,
        });
      }),
    );
  }, [
    isAssetMatchingEnabled,
    equipmentTagIndex,
    spreadsheetRowIndex,
    canCreateAssets,
    columnMapping,
    disciplineTypes,
    deliverableTypes,
    equipmentTypes,
  ]);

  // Recomputes each row's duplicate status whenever the data needed to
  // determine it changes - the existing-documents index/base targets
  // (fetched once), or anything that can change which asset a row resolves
  // to. Independent of the asset-matching/spreadsheet-backfill effect
  // above (resolves asset status fresh itself here rather than reading
  // item.assetStatus from state, which that other effect may not have
  // updated yet), so it doesn't matter which of the two effects runs
  // first. Not gated on isAssetMatchingEnabled, unlike that effect - a
  // duplicate on the upload's base Plant/Site/Project parent still matters
  // even when there's no per-row asset matching.
  useEffect(() => {
    setSelectedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (item.uploadStatus === "uploaded") return item;

        const freshAssetResolution = isAssetMatchingEnabled
          ? resolveAssetStatusForKey(item.clientDrawingNo, {
              equipmentTagIndex,
              spreadsheetRowIndex,
              canCreateAssets,
            })
          : null;

        const duplicateOf = findDuplicateForItem(
          {
            clientDrawingNo: item.clientDrawingNo,
            revNumber: item.revNumber,
            assetStatus: freshAssetResolution
              ? freshAssetResolution.assetStatus
              : item.assetStatus,
            matchedEquipmentId: freshAssetResolution
              ? freshAssetResolution.matchedEquipmentId
              : item.matchedEquipmentId,
            assetLinkSkipped: item.assetLinkSkipped,
          },
          { existingDocumentIndex, baseTargets, isAssetMatchingEnabled },
        );

        if (duplicateOf === item.duplicateOf) return item;

        return buildValidationState({ ...item, duplicateOf });
      }),
    );
  }, [
    isAssetMatchingEnabled,
    equipmentTagIndex,
    spreadsheetRowIndex,
    canCreateAssets,
    existingDocumentIndex,
    baseTargets,
  ]);

  const selectableFileIds = useMemo(
    () =>
      selectedFiles
        .filter((item) => item.uploadStatus !== "uploaded")
        .map((item) => item.id),
    [selectedFiles],
  );

  const areAllSelectableRowsSelected = useMemo(
    () =>
      selectableFileIds.length > 0 &&
      selectableFileIds.every((fileId) => selectedFileIds.includes(fileId)),
    [selectableFileIds, selectedFileIds],
  );

  const fileStats = useMemo(() => {
    const totalSize = selectedFiles.reduce((sum, item) => sum + item.size, 0);
    return {
      count: selectedFiles.length,
      totalSize,
      validCount: selectedFiles.filter(
        (item) => item.validationStatus === "valid",
      ).length,
    };
  }, [selectedFiles]);

  const pendingUploadCount = useMemo(
    () =>
      selectedFiles.filter((item) => item.uploadStatus !== "uploaded").length,
    [selectedFiles],
  );

  const createDisciplineType = async (name) => {
    try {
      const response = await api.post("/discipline-types/", { name });
      const newType = response.data;
      setDisciplineTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating discipline type:", error);
      return null;
    }
  };

  const createDeliverableType = async (name) => {
    try {
      const response = await api.post("/deliverable-types/", { name });
      const newType = response.data;
      setDeliverableTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating deliverable type:", error);
      return null;
    }
  };

  // Same endpoint/pattern NewEquipment.jsx and EditAsset.jsx already use for
  // their own "Asset Type" field (labeled the same, backed by
  // Equipment.equipmentType) - kept consistent so this dropdown behaves
  // identically to the one on the actual asset-creation form.
  const createEquipmentType = async (name) => {
    try {
      const response = await api.post("/equipment-types/", { name });
      const newType = response.data;
      setEquipmentTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating asset type:", error);
      return null;
    }
  };

  const buildUploadContextFormData = (formData) => {
    formData.append("parent_type", object);
    formData.append("parent_id", id);
    if (siteId || breadcrumbs?.site?.id) {
      formData.append("site_id", siteId || breadcrumbs?.site?.id);
    }
    if (plantId || breadcrumbs?.plant?.id) {
      formData.append("plant_id", plantId || breadcrumbs?.plant?.id);
    }
    if (projectId || breadcrumbs?.project?.id) {
      formData.append("project_id", projectId || breadcrumbs?.project?.id);
    }
    return formData;
  };

  const openSpreadsheetPicker = () => {
    if (spreadsheetInputRef.current) {
      spreadsheetInputRef.current.value = "";
      spreadsheetInputRef.current.click();
    }
  };

  const handleDownloadTemplate = async () => {
    setSpreadsheetError(null);
    setIsDownloadingTemplate(true);

    try {
      const response = await api.get(REGISTER_TEMPLATE_URL, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = REGISTER_TEMPLATE_FILENAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document register template:", error);
      setSpreadsheetError(
        error.response?.data?.detail || "Could not download the template.",
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleRemoveSpreadsheet = () => {
    setSpreadsheetFile(null);
    setSpreadsheetHeaders([]);
    setSpreadsheetRows([]);
    setSpreadsheetPreviewRows([]);
    setSpreadsheetTotalRowCount(0);
    setHeaderRowIndex(0);
    setColumnMapping({});
    setAvailableTargetFields({});
    setSpreadsheetError(null);
  };

  // Shared by the initial parse (header row 0) and by re-parsing after the
  // user picks a different header row - same file, just a different
  // header_row_index. The backend re-reads the file fresh each call rather
  // than caching a parsed workbook server-side.
  const parseSpreadsheetFile = async (file, headerRowIndexToUse) => {
    setSpreadsheetError(null);
    setIsParsingSpreadsheet(true);

    try {
      const formData = buildUploadContextFormData(new FormData());
      formData.append("spreadsheet", file);
      formData.append("header_row_index", String(headerRowIndexToUse));

      const response = await api.post(PARSE_REGISTER_SPREADSHEET_URL, formData);
      const {
        headers = [],
        rows = [],
        preview_rows: previewRows = [],
        total_row_count: totalRowCount = 0,
        suggested_mapping: suggestedMapping = {},
        available_target_fields: availableFields = {},
      } = response.data || {};

      setSpreadsheetHeaders(headers);
      setSpreadsheetRows(rows);
      setSpreadsheetPreviewRows(previewRows);
      setSpreadsheetTotalRowCount(totalRowCount);
      setHeaderRowIndex(headerRowIndexToUse);
      setAvailableTargetFields(availableFields);
      setColumnMapping(
        headers.reduce((mapping, header) => {
          mapping[header] = suggestedMapping[header] || IGNORE_MAPPING_VALUE;
          return mapping;
        }, {}),
      );
    } catch (error) {
      console.error("Error parsing document register spreadsheet:", error);
      setSpreadsheetError(
        error.response?.data?.detail || "Could not read the spreadsheet.",
      );
      setSpreadsheetHeaders([]);
      setSpreadsheetRows([]);
      setColumnMapping({});
    } finally {
      setIsParsingSpreadsheet(false);
    }
  };

  const handleSpreadsheetSelected = async (file) => {
    if (!file) return;
    setSpreadsheetFile(file);
    await parseSpreadsheetFile(file, 0);
  };

  const handleHeaderRowSelected = (rowIndex) => {
    if (!spreadsheetFile || rowIndex === headerRowIndex) return;
    parseSpreadsheetFile(spreadsheetFile, rowIndex);
  };

  const handleSpreadsheetInputChange = (event) => {
    const file = event.target.files?.[0];
    handleSpreadsheetSelected(file);
    event.target.value = "";
  };

  const handleColumnMappingChange = (header, targetKey) => {
    setColumnMapping((prev) => ({ ...prev, [header]: targetKey }));
  };

  const resetModalState = () => {
    setDefaultMetadata(buildDefaultMetadata());
    setBulkEdit(buildBulkEditState());
    setSelectedFiles([]);
    setSelectedFileIds([]);
    setFeedback(null);
    setIsLoading(false);
    handleRemoveSpreadsheet();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenModal = () => {
    resetModalState();
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetModalState();
  };

  const buildSelectedFileItem = (file) => {
    const fileBaseName = file.name.replace(/\.[^/.]+$/, "");
    const suggestedDiscipline = inferDisciplineName(file.name);
    const matchedDiscipline = disciplineTypes.find(
      (option) =>
        option.name.toLowerCase() === suggestedDiscipline.toLowerCase(),
    );

    const clientDrawingNo =
      normalizeText(defaultMetadata.clientDrawingNo) || fileBaseName;

    const assetResolution = isAssetMatchingEnabled
      ? resolveAssetStatusForKey(clientDrawingNo, {
          equipmentTagIndex,
          spreadsheetRowIndex,
          canCreateAssets,
        })
      : {
          assetStatus: "none",
          assetCandidates: [],
          matchedEquipmentId: "",
          matchedEquipment: null,
          spreadsheetRow: null,
        };

    const spreadsheetOverrides = applyMappedSpreadsheetRow({
      spreadsheetRow: assetResolution.spreadsheetRow,
      columnMapping,
      disciplineTypes,
      deliverableTypes,
      equipmentTypes,
    });

    // The spreadsheet row is the authoritative description for *this
    // document* (it's the document register) - the matched asset's own
    // description is only a fallback for when there's no spreadsheet value
    // to use (e.g. a plain filename match with no spreadsheet uploaded).
    const matchedAssetDescription = normalizeText(
      assetResolution.matchedEquipment?.description,
    );
    const hasSpreadsheetDescription = Boolean(spreadsheetOverrides.description);

    const revNumber =
      normalizeText(defaultMetadata.revNumber) ||
      spreadsheetOverrides.revNumber ||
      "0";

    const duplicateOf = findDuplicateForItem(
      {
        clientDrawingNo,
        revNumber,
        assetStatus: assetResolution.assetStatus,
        matchedEquipmentId: assetResolution.matchedEquipmentId,
        assetLinkSkipped: false,
      },
      { existingDocumentIndex, baseTargets, isAssetMatchingEnabled },
    );

    return buildValidationState({
      id: createLocalId("document"),
      uploadId: createLocalId("upload"),
      file,
      fileName: file.name,
      extension: getFileExtension(file.name).toUpperCase() || "FILE",
      size: file.size,
      clientDrawingNo,
      description:
        normalizeText(defaultMetadata.description) ||
        spreadsheetOverrides.description ||
        matchedAssetDescription ||
        fileBaseName,
      hasSpreadsheetDescription,
      discipline:
        defaultMetadata.discipline ||
        spreadsheetOverrides.discipline ||
        matchedDiscipline?.id ||
        "",
      documentType:
        defaultMetadata.documentType || spreadsheetOverrides.documentType || "",
      equipmentType: spreadsheetOverrides.equipmentType || "",
      revNumber,
      area: spreadsheetOverrides.area || inferArea(file.name),
      voteNo: spreadsheetOverrides.voteNo || "",
      invoicingProgress: "",
      efa: false,
      suggestedDiscipline,
      assetStatus: assetResolution.assetStatus,
      assetCandidates: assetResolution.assetCandidates,
      matchedEquipmentId: assetResolution.matchedEquipmentId,
      matchedEquipment: assetResolution.matchedEquipment,
      assetLinkSkipped: false,
      duplicateOf,
      createAssetIfUnmatched: assetResolution.assetStatus === "create",
      // Tracks which fields the user has explicitly edited (TextField/
      // Autocomplete/Switch/Bulk Edit), so the recompute effect above knows
      // which fields are still safe to auto-backfill from the spreadsheet
      // later, regardless of upload order. Empty here - a brand-new row has
      // no manual edits yet.
      dirtyFields: {},
      serverErrors: {},
      uploadStatus: "pending",
      progress: 0,
    });
  };

  const handleFilesSelected = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const skippedDuplicates = [];

    setSelectedFiles((prevFiles) => {
      const existingKeys = new Set(
        prevFiles.map(
          (item) =>
            `${item.file.name}-${item.size}-${item.file.lastModified}`,
        ),
      );

      const nextFiles = [...prevFiles];

      files.forEach((file) => {
        const duplicateKey = `${file.name}-${file.size}-${file.lastModified}`;
        if (existingKeys.has(duplicateKey)) {
          skippedDuplicates.push(file.name);
          return;
        }

        existingKeys.add(duplicateKey);
        nextFiles.push(buildSelectedFileItem(file));
      });

      return nextFiles;
    });

    if (skippedDuplicates.length) {
      toast({
        title: "Duplicate files skipped",
        description: skippedDuplicates.join(", "),
        status: "warning",
        duration: 3500,
        isClosable: true,
        position: "bottom",
      });
    }

    setFeedback(null);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleInputFiles = (event) => {
    handleFilesSelected(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFilesSelected(event.dataTransfer.files);
  };

  const handleDefaultMetadataChange = (field, value) => {
    setDefaultMetadata((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBulkEditChange = (field, value) => {
    setBulkEdit((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateSelectedFile = (uploadId, updates) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (item.uploadId !== uploadId) return item;

        const nextUploadStatus =
          item.uploadStatus === "error" ? "pending" : item.uploadStatus;

        // The lookup key changed - recompute this row's asset-match
        // preview rather than leaving it stale until the next unrelated
        // recompute (e.g. the equipment list finishing a later fetch).
        const assetUpdates =
          isAssetMatchingEnabled && "clientDrawingNo" in updates
            ? (() => {
                const resolution = resolveAssetStatusForKey(
                  updates.clientDrawingNo,
                  { equipmentTagIndex, spreadsheetRowIndex, canCreateAssets },
                );
                return {
                  ...resolution,
                  assetLinkSkipped: false,
                  createAssetIfUnmatched: resolution.assetStatus === "create",
                };
              })()
            : {};

        const newlyDirty = {};
        Object.keys(updates).forEach((key) => {
          if (DIRTY_TRACKABLE_FIELDS.has(key)) {
            newlyDirty[key] = true;
          }
        });

        const mergedItem = { ...item, ...updates, ...assetUpdates };

        // Anything that can change which target(s) this row links to, or
        // its own document number/revision, needs a fresh duplicate check
        // right here - not just in the external-data recompute effect -
        // so an in-row edit (typing a revision, picking a different
        // ambiguous-match asset) updates the duplicate flag immediately.
        const duplicateUpdates =
          "clientDrawingNo" in updates ||
          "revNumber" in updates ||
          "matchedEquipmentId" in updates ||
          "assetLinkSkipped" in updates ||
          Object.keys(assetUpdates).length > 0
            ? {
                duplicateOf: findDuplicateForItem(mergedItem, {
                  existingDocumentIndex,
                  baseTargets,
                  isAssetMatchingEnabled,
                }),
              }
            : {};

        return buildValidationState({
          ...mergedItem,
          ...duplicateUpdates,
          dirtyFields: { ...item.dirtyFields, ...newlyDirty },
          serverErrors: {},
          uploadStatus: nextUploadStatus,
          progress: nextUploadStatus === "pending" ? 0 : item.progress,
        });
      }),
    );
  };

  const applyBulkEdit = (scope) => {
    const updates = {};

    if (bulkEdit.discipline) {
      updates.discipline = bulkEdit.discipline;
    }
    if (bulkEdit.documentType) {
      updates.documentType = bulkEdit.documentType;
    }
    if (bulkEdit.revNumber !== "") {
      updates.revNumber = bulkEdit.revNumber;
    }
    if (bulkEdit.equipmentType) {
      updates.equipmentType = bulkEdit.equipmentType;
    }

    if (!Object.keys(updates).length) {
      return;
    }

    const targetIds =
      scope === "selected"
        ? selectedFileIds.filter((fileId) => selectableFileIds.includes(fileId))
        : selectableFileIds;

    if (scope === "selected" && !targetIds.length) {
      setFeedback({
        severity: "warning",
        message: NO_SELECTED_ROWS_MESSAGE,
      });
      toast({
        title: "Selection required",
        description: NO_SELECTED_ROWS_MESSAGE,
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    const newlyDirty = {};
    Object.keys(updates).forEach((key) => {
      if (DIRTY_TRACKABLE_FIELDS.has(key)) {
        newlyDirty[key] = true;
      }
    });

    setSelectedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (!targetIds.includes(item.id)) return item;

        const mergedItem = { ...item, ...updates };
        // Bulk edit can change revNumber, which is part of the duplicate
        // key - recompute here too, same reason as updateSelectedFile.
        const duplicateUpdates =
          "revNumber" in updates
            ? {
                duplicateOf: findDuplicateForItem(mergedItem, {
                  existingDocumentIndex,
                  baseTargets,
                  isAssetMatchingEnabled,
                }),
              }
            : {};

        return buildValidationState({
          ...mergedItem,
          ...duplicateUpdates,
          dirtyFields: { ...item.dirtyFields, ...newlyDirty },
          serverErrors: {},
          uploadStatus:
            item.uploadStatus === "error" ? "pending" : item.uploadStatus,
          progress: item.uploadStatus === "error" ? 0 : item.progress,
        });
      }),
    );
    setFeedback(null);
  };

  const handleToggleSelection = (fileId) => {
    setSelectedFileIds((prevSelected) =>
      prevSelected.includes(fileId)
        ? prevSelected.filter((id) => id !== fileId)
        : [...prevSelected, fileId],
    );
  };

  const handleToggleSelectAll = () => {
    if (areAllSelectableRowsSelected) {
      setSelectedFileIds((prevSelected) =>
        prevSelected.filter((fileId) => !selectableFileIds.includes(fileId)),
      );
      return;
    }

    setSelectedFileIds((prevSelected) => {
      const nextSelection = new Set(prevSelected);
      selectableFileIds.forEach((fileId) => nextSelection.add(fileId));
      return Array.from(nextSelection);
    });
  };

  const handleRemoveFile = (uploadId) => {
    const removedFileId = selectedFiles.find(
      (item) => item.uploadId === uploadId,
    )?.id;

    setSelectedFiles((prevFiles) =>
      prevFiles.filter((item) => item.uploadId !== uploadId),
    );
    setSelectedFileIds((prevSelected) =>
      prevSelected.filter((fileId) => fileId !== removedFileId),
    );
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setFeedback(null);

    try {
      const validatedFiles = selectedFiles.map((item) =>
        buildValidationState(item),
      );
      setSelectedFiles(validatedFiles);

      const filesToUpload = validatedFiles.filter(
        (item) => item.uploadStatus !== "uploaded",
      );

      if (!filesToUpload.length) {
        setFeedback({
          severity: "info",
          message: "No pending documents to upload.",
        });
        return;
      }

      const invalidFiles = filesToUpload.filter(
        (item) => item.validationStatus === "invalid",
      );

      if (invalidFiles.length) {
        setFeedback({
          severity: "error",
          message:
            "Please fix the validation errors in the selected document rows before uploading.",
        });
        return;
      }

      const formData = new FormData();
      filesToUpload.forEach((item) => {
        formData.append("documents", item.file);
        formData.append("document_upload_ids", item.uploadId);
      });

      const metadataArray = filesToUpload.map((item) => ({
        client_drawing_no: normalizeText(item.clientDrawingNo),
        description: normalizeText(item.description),
        discipline_type: Number(item.discipline),
        document_type: Number(item.documentType),
        revision_no: item.revNumber ?? "",
        area: item.area || "",
        vote_no: item.voteNo || "",
        invoicing_progress: item.invoicingProgress || "",
        efa: Boolean(item.efa),
        fileRefs: [item.uploadId],
        ...(isAssetMatchingEnabled
          ? {
              equipment_id:
                item.matchedEquipmentId && !item.assetLinkSkipped
                  ? item.matchedEquipmentId
                  : undefined,
              create_asset_if_unmatched:
                item.assetStatus === "create" && item.createAssetIfUnmatched,
              equipment_type_id:
                item.assetStatus === "create" && item.equipmentType
                  ? item.equipmentType
                  : undefined,
            }
          : {}),
      }));

      if (process.env.NODE_ENV !== "production") {
        console.log("FORMDATA FILES", selectedFiles);
        console.log("FORMDATA METADATA", metadataArray);
      }

      formData.append(
        "document_metadata",
        JSON.stringify(metadataArray),
      );

      formData.append("parent_type", object);
      formData.append("parent_id", id);

      if (siteId || breadcrumbs?.site?.id) {
        formData.append("site_id", siteId || breadcrumbs?.site?.id);
      }
      if (plantId || breadcrumbs?.plant?.id) {
        formData.append("plant_id", plantId || breadcrumbs?.plant?.id);
      }
      if (projectId || breadcrumbs?.project?.id) {
        formData.append("project_id", projectId || breadcrumbs?.project?.id);
      }

      formData.append("deliverableStatus", null);

      if (isAssetMatchingEnabled) {
        formData.append("match_assets", "true");
      }

      const uploadIds = new Set(filesToUpload.map((item) => item.uploadId));
      setSelectedFiles((prevFiles) =>
        prevFiles.map((item) =>
          uploadIds.has(item.uploadId)
            ? {
                ...item,
                uploadStatus: "uploading",
                progress: 0,
              }
            : item,
        ),
      );

      const response = await api.post(
        `${DELIVERABLES_URL}bulk-upload/`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percent = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            setSelectedFiles((prevFiles) =>
              prevFiles.map((item) =>
                uploadIds.has(item.uploadId)
                  ? {
                      ...item,
                      uploadStatus: "uploading",
                      progress: percent,
                    }
                  : item,
              ),
            );
          },
        },
      );

      if (process.env.NODE_ENV !== "production") {
        console.log("UPLOAD RESPONSE", response.status, response.data);
      }

      const {
        uploaded_files: uploadedFiles = [],
        failed_files: failedFiles = [],
        created_documents: createdDocuments = [],
        created_or_linked_assets: createdOrLinkedAssets = [],
        ambiguous_asset_matches: ambiguousAssetMatches = [],
      } = response.data || {};

      const newAssetCount = createdOrLinkedAssets.filter(
        (entry) => entry.created,
      ).length;
      const linkedAssetCount = createdOrLinkedAssets.length - newAssetCount;
      const assetSummaryParts = [];
      if (newAssetCount) {
        assetSummaryParts.push(
          `${newAssetCount} new asset(s) created`,
        );
      }
      if (linkedAssetCount) {
        assetSummaryParts.push(
          `${linkedAssetCount} document(s) linked to existing asset(s)`,
        );
      }
      if (ambiguousAssetMatches.length) {
        assetSummaryParts.push(
          `${ambiguousAssetMatches.length} row(s) had an ambiguous asset match and were not linked`,
        );
      }
      const duplicateCount = failedFiles.filter(
        (entry) => entry.errors?.duplicate,
      ).length;
      if (duplicateCount) {
        assetSummaryParts.push(
          `${duplicateCount} document(s) already existed and were not uploaded`,
        );
      }
      const assetSummary = assetSummaryParts.length
        ? ` ${assetSummaryParts.join("; ")}.`
        : "";

      const uploadedMap = new Map(
        uploadedFiles.map((item) => [item.upload_id, item]),
      );
      const failedMap = new Map(
        failedFiles.map((item) => [item.upload_id, item]),
      );
      const uploadedRowIds = new Set(
        filesToUpload
          .filter((item) => uploadedMap.has(item.uploadId))
          .map((item) => item.id),
      );

      setSelectedFiles((prevFiles) =>
        prevFiles.map((item) => {
          if (uploadedMap.has(item.uploadId)) {
            return {
              ...item,
              serverErrors: {},
              uploadStatus: "uploaded",
              progress: 100,
            };
          }

          if (failedMap.has(item.uploadId)) {
            return buildValidationState({
              ...item,
              serverErrors: failedMap.get(item.uploadId)?.errors || {},
              uploadStatus: "error",
              progress: 0,
            });
          }

          return item;
        }),
      );
      setSelectedFileIds((prevSelected) =>
        prevSelected.filter((fileId) => !uploadedRowIds.has(fileId)),
      );

      await fetchData?.();

      if (!failedFiles.length) {
        toast({
          title: "Success",
          description: `${createdDocuments.length} document record(s) created successfully.${assetSummary}`,
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
        handleCloseModal();
        return;
      }

      if (createdDocuments.length > 0) {
        setFeedback({
          severity: "warning",
          message: `${createdDocuments.length} document record(s) created, but ${failedFiles.length} file row(s) still need attention.${assetSummary}`,
        });
        toast({
          title: "Partial success",
          description: `${createdDocuments.length} document record(s) created and ${failedFiles.length} row(s) failed.${assetSummary}`,
          status: "warning",
          duration: 4500,
          isClosable: true,
          position: "bottom",
        });
        return;
      }

      setFeedback({
        severity: "error",
        message:
          response.data?.message ||
          "No documents were created. Review the failed document rows below.",
      });
      toast({
        title: "Upload failed",
        description:
          response.data?.message || "No documents were created.",
        status: "error",
        duration: 4500,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.log("UPLOAD ERROR", error.response?.status, error.response?.data);
      }

      const failedFiles = error.response?.data?.failed_files || [];
      const failedMap = new Map(
        failedFiles.map((item) => [item.upload_id, item]),
      );

      setSelectedFiles((prevFiles) =>
        prevFiles.map((item) => {
          if (failedMap.has(item.uploadId)) {
            return buildValidationState({
              ...item,
              serverErrors: failedMap.get(item.uploadId)?.errors || {},
              uploadStatus: "error",
              progress: 0,
            });
          }

          if (item.uploadStatus === "uploading") {
            return {
              ...item,
              uploadStatus: "pending",
              progress: 0,
            };
          }

          return item;
        }),
      );

      setFeedback({
        severity: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Unexpected error adding documents.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Button
        onClick={handleOpenModal}
        variant="contained"
        size="small"
        sx={{
          bgcolor: "#24b2ab",
          color: "#fff",
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9rem",
          height: 36,
          px: 2,
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        New Documents
      </Button>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "97%", md: "94%" },
            bgcolor: "background.paper",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "95vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
            >
              New Documents
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box
            sx={{
              overflowY: "auto",
              flexGrow: 1,
              px: 3,
              py: 2.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "grey.400",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={FILE_INPUT_ACCEPT}
              onChange={handleInputFiles}
              style={{ display: "none" }}
            />

            <Box
              onDrop={handleDrop}
              onDragOver={(event) => event.preventDefault()}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: 200,
              }}
            >
              {feedback && (
                <Alert severity={feedback.severity} sx={{ borderRadius: "12px" }}>
                  {feedback.message}
                </Alert>
              )}

              {/* <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
                >
                  Default Metadata For New Files
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  The values in this row will be copied into newly selected files by default.
                </Typography>

                <TableContainer
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "auto",
                    "&::-webkit-scrollbar": { width: "4px", height: "4px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "grey.400",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          "Client Drawing No.",
                          "Description",
                          "Discipline Type",
                          "Document Type",
                          "Revision No.",
                          "File",
                        ].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              color: "#fff",
                              bgcolor: "primary.main",
                              py: 1.5,
                              px: 2,
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell>
                          <TextField
                            label="Drawing No."
                            value={defaultMetadata.clientDrawingNo}
                            onChange={(event) =>
                              handleDefaultMetadataChange(
                                "clientDrawingNo",
                                event.target.value,
                              )
                            }
                            fullWidth
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                              },
                              minWidth: 150,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            label="Description"
                            value={defaultMetadata.description}
                            onChange={(event) =>
                              handleDefaultMetadataChange(
                                "description",
                                event.target.value,
                              )
                            }
                            fullWidth
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                              },
                              minWidth: 180,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <MetadataAutocomplete
                            label="Discipline Type"
                            value={defaultMetadata.discipline}
                            options={disciplineTypes}
                            onCreate={createDisciplineType}
                            onChange={(value) =>
                              handleDefaultMetadataChange("discipline", value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MetadataAutocomplete
                            label="Document Type"
                            value={defaultMetadata.documentType}
                            options={deliverableTypes}
                            onCreate={createDeliverableType}
                            onChange={(value) =>
                              handleDefaultMetadataChange("documentType", value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            label="Revision No."
                            value={defaultMetadata.revNumber}
                            onChange={(event) =>
                              handleDefaultMetadataChange(
                                "revNumber",
                                event.target.value,
                              )
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                              },
                              minWidth: 120,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 190 }}>
                          <Button
                            variant="outlined"
                            startIcon={<FileUploadIcon />}
                            onClick={openFilePicker}
                            sx={{
                              textTransform: "none",
                              borderRadius: "10px",
                              borderColor: "primary.main",
                              color: "primary.main",
                              fontWeight: 600,
                            }}
                          >
                            Select Files
                          </Button>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.75 }}
                          >
                            {fileStats.count} selected
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box> */}

              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "primary.main",
                  borderRadius: "18px",
                  bgcolor: "rgba(0,141,138,0.04)",
                  px: 3,
                  py: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1.25,
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 42, color: "primary.main" }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Drag and drop files here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  New files will inherit the current default metadata row.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported types: PDF, DOC, DOCX, XLS, XLSX, CSV, PNG, JPG, JPEG. Maximum file size: {MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<FileUploadIcon />}
                  onClick={openFilePicker}
                  sx={{
                    mt: 1,
                    bgcolor: "primary.main",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2.5,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  Select Files
                </Button>
              </Box>

              {isAssetMatchingEnabled && (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "14px",
                    px: 2,
                    py: 2,
                    bgcolor: "grey.50",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TableChartOutlinedIcon sx={{ color: "primary.main" }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "primary.main" }}
                    >
                      Also upload a document register spreadsheet (optional)
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Documents are always matched to existing assets by filename
                    automatically.{" "}
                    {canCreateAssets
                      ? "Upload a spreadsheet here to also create a new asset for any file that doesn't match one."
                      : "Creating new assets isn't available at this scope — upload a spreadsheet here only to auto-fill document metadata."}
                  </Typography>

                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                      <input
                        ref={spreadsheetInputRef}
                        type="file"
                        accept=".xlsx,.xlsm,.csv"
                        onChange={handleSpreadsheetInputChange}
                        style={{ display: "none" }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        {!spreadsheetFile ? (
                          <Button
                            variant="outlined"
                            startIcon={<TableChartOutlinedIcon />}
                            onClick={openSpreadsheetPicker}
                            disabled={isParsingSpreadsheet}
                            sx={{
                              textTransform: "none",
                              borderRadius: "10px",
                              fontWeight: 600,
                              borderColor: "primary.main",
                              color: "primary.main",
                            }}
                          >
                            {isParsingSpreadsheet
                              ? "Reading spreadsheet…"
                              : "Select Spreadsheet"}
                          </Button>
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Chip
                              icon={<TableChartOutlinedIcon />}
                              label={spreadsheetFile.name}
                              onDelete={handleRemoveSpreadsheet}
                              sx={{ fontWeight: 600 }}
                            />
                            {isParsingSpreadsheet && <CircularProgress size={18} />}
                          </Box>
                        )}
                        <Button
                          variant="text"
                          startIcon={
                            isDownloadingTemplate ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DownloadOutlined />
                            )
                          }
                          onClick={handleDownloadTemplate}
                          disabled={isDownloadingTemplate}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Download Template
                        </Button>
                      </Box>

                      {spreadsheetError && (
                        <Alert severity="error" sx={{ borderRadius: "12px" }}>
                          {spreadsheetError}
                        </Alert>
                      )}

                      {spreadsheetPreviewRows.length > 1 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Which row has your real column names?
                          </Typography>
                          <TableContainer
                            sx={{
                              maxHeight: 200,
                              overflow: "auto",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: "10px",
                            }}
                          >
                            <Table size="small" stickyHeader>
                              <TableBody>
                                {spreadsheetPreviewRows.map((row, rowIndex) => (
                                  <TableRow
                                    key={rowIndex}
                                    hover
                                    selected={rowIndex === headerRowIndex}
                                    onClick={() => handleHeaderRowSelected(rowIndex)}
                                    sx={{ cursor: isParsingSpreadsheet ? "default" : "pointer" }}
                                  >
                                    <TableCell padding="checkbox">
                                      <Radio
                                        checked={rowIndex === headerRowIndex}
                                        size="small"
                                        disabled={isParsingSpreadsheet}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{rowIndex + 1}</TableCell>
                                    {row.map((cell, cellIndex) => (
                                      <TableCell key={cellIndex} sx={{ whiteSpace: "nowrap" }}>
                                        {cell}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Pick the row that has your real column names (e.g. "Area",
                            "Document No.") — not one like this that shows generic
                            placeholders such as "F1, F2, F3".
                            {spreadsheetTotalRowCount > spreadsheetPreviewRows.length &&
                              ` Showing the first ${spreadsheetPreviewRows.length} of ${spreadsheetTotalRowCount} rows.`}
                          </Typography>
                        </Box>
                      )}

                      {spreadsheetHeaders.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Column mapping — {spreadsheetRows.length} row(s) found
                          </Typography>
                          <TableContainer
                            sx={{
                              overflowX: "auto",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: "10px",
                            }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {spreadsheetHeaders.map((header) => (
                                    <TableCell
                                      key={header}
                                      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                                      title={header}
                                    >
                                      {header}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  {spreadsheetHeaders.map((header) => (
                                    <TableCell key={header} sx={{ minWidth: 170 }}>
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={columnMapping[header] || IGNORE_MAPPING_VALUE}
                                          onChange={(event) =>
                                            handleColumnMappingChange(header, event.target.value)
                                          }
                                          sx={{ borderRadius: "8px" }}
                                        >
                                          <MenuItem value={IGNORE_MAPPING_VALUE}>Ignore</MenuItem>
                                          {Object.entries(availableTargetFields).map(
                                            ([key, label]) => (
                                              <MenuItem key={key} value={key}>
                                                {label}
                                              </MenuItem>
                                            ),
                                          )}
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 1 }}
                          >
                            Map a column to "Client Drawing No. (Document No.)" so
                            spreadsheet rows match uploaded files by filename.
                            {!documentNoColumnHeader && " Not mapped yet."}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Selected Files
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fileStats.count} file(s) selected • {formatFileSize(fileStats.totalSize)}
                  </Typography>
                </Box>
                <Chip
                  label={`${fileStats.validCount} valid • ${pendingUploadCount} pending`}
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: "999px", fontWeight: 600 }}
                />
              </Box>

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "14px",
                  px: 2,
                  py: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}
                >
                  Bulk Edit
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.25,
                    alignItems: "center",
                  }}
                >
                  <MetadataAutocomplete
                    label="Discipline Type"
                    value={bulkEdit.discipline}
                    options={disciplineTypes}
                    onCreate={createDisciplineType}
                    onChange={(value) =>
                      handleBulkEditChange("discipline", value)
                    }
                    minWidth={180}
                  />
                  <MetadataAutocomplete
                    label="Document Type"
                    value={bulkEdit.documentType}
                    options={deliverableTypes}
                    onCreate={createDeliverableType}
                    onChange={(value) =>
                      handleBulkEditChange("documentType", value)
                    }
                    minWidth={180}
                  />
                  <TextField
                    label="Revision No."
                    value={bulkEdit.revNumber}
                    onChange={(event) =>
                      handleBulkEditChange("revNumber", event.target.value)
                    }
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                      minWidth: 140,
                    }}
                  />
                  {canCreateAssets && spreadsheetFile && (
                    <MetadataAutocomplete
                      label="Asset Type"
                      value={bulkEdit.equipmentType}
                      options={equipmentTypes}
                      onCreate={createEquipmentType}
                      onChange={(value) =>
                        handleBulkEditChange("equipmentType", value)
                      }
                      minWidth={180}
                    />
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => applyBulkEdit("all")}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      color: "primary.main",
                      borderColor: "primary.main",
                    }}
                  >
                    Apply to All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => applyBulkEdit("selected")}
                    sx={{
                      bgcolor: "primary.main",
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    Apply to Selected
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1.25 }}
                >
                  {BULK_EDIT_HELPER_TEXT}
                </Typography>
              </Box>

              <TableContainer
                sx={{
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "auto",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={areAllSelectableRowsSelected}
                          indeterminate={
                            selectedFileIds.length > 0 &&
                            !areAllSelectableRowsSelected
                          }
                          onChange={handleToggleSelectAll}
                          disabled={!selectableFileIds.length || isLoading}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Client Drawing No.
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Discipline Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Document Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Revision No.</TableCell>
                      {isAssetMatchingEnabled && (
                        <TableCell sx={{ fontWeight: 700 }}>Asset</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700 }}>File Size</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Upload Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Validation</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedFiles.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={isAssetMatchingEnabled ? 13 : 12}
                          sx={{ py: 4, textAlign: "center" }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No files selected yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedFiles.map((item) => {
                        const isUploaded = item.uploadStatus === "uploaded";
                        const validationMessage =
                          flattenErrors(item.validationErrors) || "Valid";

                        return (
                          <TableRow key={item.uploadId}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedFileIds.includes(item.id)}
                                onChange={() => handleToggleSelection(item.id)}
                                disabled={isUploaded || isLoading}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 200 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.25,
                                }}
                              >
                                <InsertDriveFileOutlinedIcon
                                  sx={{ color: "primary.main" }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {item.fileName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block" }}
                                  >
                                    {item.file.type || "Unknown file type"}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ minWidth: 170 }}>
                              <TextField
                                value={item.clientDrawingNo}
                                onChange={(event) =>
                                  updateSelectedFile(item.uploadId, {
                                    clientDrawingNo: event.target.value,
                                  })
                                }
                                size="small"
                                disabled={isUploaded || isLoading}
                                error={Boolean(item.validationErrors.clientDrawingNo)}
                                helperText={item.validationErrors.clientDrawingNo || " "}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 190 }}>
                              <TextField
                                value={item.description}
                                onChange={(event) =>
                                  updateSelectedFile(item.uploadId, {
                                    description: event.target.value,
                                  })
                                }
                                size="small"
                                disabled={isUploaded || isLoading}
                                error={Boolean(item.validationErrors.description)}
                                helperText={item.validationErrors.description || " "}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <MetadataAutocomplete
                                label="Discipline Type"
                                value={item.discipline}
                                options={disciplineTypes}
                                onCreate={createDisciplineType}
                                onChange={(value) =>
                                  updateSelectedFile(item.uploadId, {
                                    discipline: value,
                                  })
                                }
                                disabled={isUploaded || isLoading}
                                minWidth={170}
                              />
                              {item.validationErrors.discipline && (
                                <Typography
                                  variant="caption"
                                  color="error.main"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  {item.validationErrors.discipline}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <MetadataAutocomplete
                                label="Document Type"
                                value={item.documentType}
                                options={deliverableTypes}
                                onCreate={createDeliverableType}
                                onChange={(value) =>
                                  updateSelectedFile(item.uploadId, {
                                    documentType: value,
                                  })
                                }
                                disabled={isUploaded || isLoading}
                                minWidth={170}
                              />
                              {item.validationErrors.documentType && (
                                <Typography
                                  variant="caption"
                                  color="error.main"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  {item.validationErrors.documentType}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 135 }}>
                              <TextField
                                value={item.revNumber}
                                onChange={(event) =>
                                  updateSelectedFile(item.uploadId, {
                                    revNumber: event.target.value,
                                  })
                                }
                                size="small"
                                disabled={isUploaded || isLoading}
                                helperText=" "
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                  },
                                }}
                              />
                            </TableCell>
                            {isAssetMatchingEnabled && (
                              <TableCell sx={{ minWidth: 220 }}>
                                {item.assetStatus === "existing" &&
                                  (() => {
                                    const matched =
                                      item.matchedEquipment ||
                                      equipmentList.find(
                                        (equipment) =>
                                          String(equipment.id) ===
                                          String(item.matchedEquipmentId),
                                      );
                                    return (
                                      <Chip
                                        label={`Linked to existing: ${matched?.tagNumber || item.matchedEquipmentId}`}
                                        color="success"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    );
                                  })()}
                                {item.assetStatus === "create" && (
                                  <Box>
                                    <Chip
                                      label={`Will create new asset: ${item.clientDrawingNo}`}
                                      color="primary"
                                      variant="outlined"
                                      size="small"
                                      sx={{ fontWeight: 600 }}
                                    />
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.5,
                                      }}
                                    >
                                      <Switch
                                        size="small"
                                        checked={Boolean(item.createAssetIfUnmatched)}
                                        onChange={(event) =>
                                          updateSelectedFile(item.uploadId, {
                                            createAssetIfUnmatched: event.target.checked,
                                          })
                                        }
                                        disabled={isUploaded || isLoading}
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        Create asset
                                      </Typography>
                                    </Box>
                                    {item.createAssetIfUnmatched &&
                                      (item.equipmentType ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: "block" }}
                                        >
                                          Asset Type:{" "}
                                          {equipmentTypes.find(
                                            (type) =>
                                              String(type.id) === String(item.equipmentType),
                                          )?.name || item.equipmentType}
                                        </Typography>
                                      ) : (
                                        <Typography
                                          variant="caption"
                                          color="error.main"
                                          sx={{ display: "block" }}
                                        >
                                          Asset Type required
                                        </Typography>
                                      ))}
                                  </Box>
                                )}
                                {item.assetStatus === "ambiguous" && (
                                  <FormControl
                                    size="small"
                                    fullWidth
                                    error={Boolean(item.validationErrors.asset)}
                                  >
                                    <Select
                                      value={
                                        item.assetLinkSkipped
                                          ? "skip"
                                          : item.matchedEquipmentId || ""
                                      }
                                      displayEmpty
                                      onChange={(event) => {
                                        const { value } = event.target;
                                        if (value === "skip") {
                                          updateSelectedFile(item.uploadId, {
                                            matchedEquipmentId: "",
                                            matchedEquipment: null,
                                            assetLinkSkipped: true,
                                          });
                                          return;
                                        }

                                        const pickedCandidate = item.assetCandidates.find(
                                          (candidate) => String(candidate.id) === value,
                                        );
                                        updateSelectedFile(item.uploadId, {
                                          matchedEquipmentId: value,
                                          matchedEquipment: pickedCandidate || null,
                                          assetLinkSkipped: false,
                                          // The spreadsheet's description (if
                                          // this row has one) stays
                                          // authoritative - the picked
                                          // asset's own description only
                                          // fills in when there isn't one.
                                          description: item.hasSpreadsheetDescription
                                            ? item.description
                                            : normalizeText(pickedCandidate?.description) ||
                                              item.description,
                                        });
                                      }}
                                      disabled={isUploaded || isLoading}
                                      sx={{ borderRadius: "8px" }}
                                    >
                                      <MenuItem value="" disabled>
                                        Ambiguous match — pick one
                                      </MenuItem>
                                      {item.assetCandidates.map((candidate) => (
                                        <MenuItem key={candidate.id} value={String(candidate.id)}>
                                          {candidate.tagNumber}
                                          {candidate.equipmentName &&
                                          candidate.equipmentName !== candidate.tagNumber
                                            ? ` — ${candidate.equipmentName}`
                                            : ""}
                                        </MenuItem>
                                      ))}
                                      <MenuItem value="skip">No asset (skip linking)</MenuItem>
                                    </Select>
                                    {item.validationErrors.asset && (
                                      <Typography
                                        variant="caption"
                                        color="error.main"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {item.validationErrors.asset}
                                      </Typography>
                                    )}
                                  </FormControl>
                                )}
                                {item.assetStatus === "none" && (
                                  <Chip label="No asset" size="small" sx={{ fontWeight: 600 }} />
                                )}
                              </TableCell>
                            )}
                            <TableCell>{formatFileSize(item.size)}</TableCell>
                            <TableCell>{item.extension}</TableCell>
                            <TableCell sx={{ minWidth: 170 }}>
                              {item.uploadStatus === "uploading" ? (
                                <Box sx={{ minWidth: 140 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Uploading {item.progress}%
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={item.progress}
                                    sx={{ mt: 0.5, borderRadius: 99, height: 6 }}
                                  />
                                </Box>
                              ) : item.uploadStatus === "uploaded" ? (
                                <Chip
                                  label="Uploaded"
                                  color="success"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              ) : item.uploadStatus === "error" ? (
                                <Chip
                                  label="Failed"
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              ) : (
                                <Chip
                                  label="Pending"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <Typography
                                variant="body2"
                                color={
                                  item.validationStatus === "invalid"
                                    ? "error.main"
                                    : "success.main"
                                }
                              >
                                {validationMessage}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Remove file">
                                <span>
                                  <IconButton
                                    onClick={() => handleRemoveFile(item.uploadId)}
                                    disabled={isUploaded || isLoading}
                                    sx={{
                                      color: "error.main",
                                      "&:hover": { bgcolor: "action.hover" },
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  pt: 1,
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCloseModal}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isLoading || selectedFiles.length === 0}
                  sx={{
                    bgcolor: "primary.main",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 120,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AddDocuments;
